import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import styles from "./styles.module.scss";

/**
 * ControlButton - A functional component for rendering a control button.
 *
 * This component displays a button for controlling the predictions in a plate.
 * It includes logic to enable or disable the button based on certain conditions.
 *
 * @param {Object} props - The props passed to the component.
 * @param {string} props.button_type - The type of the button (plus or minus).
 * @param {string} props.button_side - The side of the button (left or right).
 * @param {string} props.plate_type - The type of the plate being controlled.
 * @param {string} props.plate_side - The side of the plate being controlled (pminus or pplus).
 * @param {Object} props.predictions - The current state of predictions.
 * @param {Function} props.onClickMethod - The method to call on button click.
 */
const ControlButton = ({
  button_type,
  button_side,
  plate_type,
  plate_side,
  predictions,
  onClickMethod,
}) => {
  // State to manage the status of the button (active or disabled)
  const [button_status, setButtonStatus] = useState("active");
  const intl = useIntl();

  /**
   * Checks the current state and sets the button status accordingly.
   */
  const checkAndSetButtonStatus = () => {
    const plate_value = predictions[plate_type][plate_side];
    if (
      (button_type === "plus" &&
        plate_type === "lb-amp-ara" &&
        plate_value === 5) ||
      (button_type === "minus" && plate_value === 0) ||
      (button_type === "plus" && plate_value === 6)
    ) {
      setButtonStatus("disabled");
    } else {
      setButtonStatus("active");
    }
  };

  // Effect hook to update the button status based on dependencies.
  useEffect(() => {
    checkAndSetButtonStatus();
  }, [button_type, plate_side, predictions, plate_type]);

  /**
   * Handles key down events for accessibility purposes.
   *
   * @param {Object} e - The event object.
   */
  const handleKeyDown = (e) => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      onClickMethod(
        plate_type,
        plate_side,
        predictions[plate_type][plate_side],
        button_type
      );
    }
  };

  return (
    <div className={styles.controlButtonsImg}>
      <img
        role="presentation"
        draggable="false"
        alt={
          button_type === "plus"
            ? intl.formatMessage({
                id: "Increase growth in the LB/AMP/ARA plate.",
                defaultMessage: "Increase growth in the LB/AMP/ARA plate.",
                description: "",
              })
            : intl.formatMessage({
                id: "Decrease growth in the LB/AMP/ARA plate.",
                defaultMessage: "Decrease growth in the LB/AMP/ARA plate.",
                description: "",
              })
        }
        onClick={() =>
          onClickMethod(
            plate_type,
            plate_side,
            predictions[plate_type][plate_side],
            button_type
          )
        }
        onKeyDown={handleKeyDown}
        onMouseDown={() =>
          button_status !== "disabled" ? setButtonStatus("pressed") : null
        }
        onMouseUp={() => checkAndSetButtonStatus()}
        onMouseOut={() => checkAndSetButtonStatus()}
        onBlur={() => checkAndSetButtonStatus()}
        src={
          require(
            `./img/buttons/${button_status}-${button_side}-${button_type}.svg`
          ).default
        }
      />
    </div>
  );
};

export default ControlButton;
